import { type JSX,useCallback, useLayoutEffect } from 'react';

import { useTheme } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import {
    selectCurrentEntity,
    selectCurrentTenantId
} from 'common/components/EditDrawer/editDrawerSlice';
import { selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { PostAclEntryThunkParams, PostAclEntryThunkResponse, PutAclEntryThunkParams, PutAclEntryThunkResponse } from 'common/services/api/Acls/AclEntitiesApiTypeDefs';
import { postAclEntry, putAclEntry } from 'common/services/api/Acls/AclEntries.thunk';
import { AclFormatted } from 'common/types/Entities';
import {
    DrawerEntitiesNames,
    EditDrawerContentProps,
    Operations
} from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';

import {
    CreateStateHandlerResult,
    EditStateHandlerResult,
    useCreateStateHandler,
    useEditStateHandler } from './AclEntriesDrawerContentStateHandlers';
import { AclEntriesDrawerProperties,AclEntriesPage } from './aclEntriesTypeDefs';

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'principalName', sort: 'asc' }];

function useAclEntriesDrawer(
    drawerProps: AclEntriesDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<AclFormatted>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const drawerContext = useDrawerContext<
        AclFormatted,
        AclEntriesDrawerProperties,
        PostAclEntryThunkParams,
        PostAclEntryThunkResponse,
        PutAclEntryThunkParams,
        PutAclEntryThunkResponse
    >('ACL Entry', drawerProps, editingItemInfo);

    const paginationInfo = useAppSelector(selectPaginationGridData) ?? { currentPage: 0, rowsPerPage: 0 };
    const rowsPerPage = paginationInfo.rowsPerPage ?? 10;
    const sortModel = paginationInfo.sortModel ?? DEFAULT_SORT_MODEL;

    const createState = useCreateStateHandler(drawerProps, theme);
    const editState = useEditStateHandler(
        drawerProps,
        editingItemInfo?.editingRow as AclFormatted
    );

    const state: CreateStateHandlerResult | EditStateHandlerResult =
        drawerContext.action === Operations.Create || drawerContext.action === Operations.Link
            ? createState
            : editState;

    useLayoutEffect(() => {
        if (drawerContext.currentId === DrawerIDs.AclEntries || drawerContext.currentId === DrawerIDs.UserGroupAclEntries
            || drawerContext.currentId === DrawerIDs.UserProfileAcls){
            state.onOpenChange(drawerContext.isOpenDrawer);
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId]);

    const createSuccessHandler = useCallback((postResult: PostAclEntryThunkResponse) => {
        if (drawerProps.aclPage === AclEntriesPage.License) {
            drawerContext.createSuccessHandler(postResult);
            dispatch(setPaginationGridData({
                rows: postResult.rows,
                rowsCount: postResult.count,
                currentPage: postResult?.skipped ? (postResult.skipped / rowsPerPage) : 0,
                rowsPerPage: rowsPerPage,
                sortModel: sortModel
            }));
        } else {
            drawerContext.createSuccessHandler(postResult);
            state.successHandler(postResult);
        }
    }, [drawerContext, dispatch, drawerProps.aclPage, rowsPerPage, sortModel, state]);

    const editSuccessHandler = useCallback((putResult: PutAclEntryThunkResponse) => {
        if (drawerProps.aclPage === AclEntriesPage.License) {
            drawerContext.editSuccessHandler(putResult);
            dispatch(setPaginationGridData({
                rows: putResult.rows,
                rowsCount: putResult.count,
                currentPage: putResult?.skipped ? (putResult.skipped / rowsPerPage) : 0,
                rowsPerPage: rowsPerPage,
                sortModel: sortModel
            }));
        } else {
            drawerContext.editSuccessHandler(putResult);
            state.successHandler(putResult);
        }
    }, [drawerContext, dispatch, drawerProps.aclPage, rowsPerPage, sortModel, state]);

    const getPostOptions = useCallback(() => {
        return state.getOptions() as PostAclEntryThunkParams;
    }, [state]);

    const getPutOptions = useCallback(() => {
        return state.getOptions() as PutAclEntryThunkParams;
    }, [state]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postAclEntry, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putAclEntry, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    const getCreateContent = useCallback(() => {
        return createState.getContent(
            postCallback,
            drawerContext.processing,
            drawerContext.nameInConflict,
            drawerContext.conflictErrorOrigin
        );
    }, [createState, postCallback, drawerContext.processing, drawerContext.nameInConflict, drawerContext.conflictErrorOrigin]);

    const getEditContent = useCallback(() => {
        return editState.getContent(
            putCallback,
            drawerContext.processing,
            drawerContext.nameInConflict
        );
    }, [editState, putCallback, drawerContext.processing, drawerContext.nameInConflict]);

    return {
        ...drawerContext,
        createState,
        editState,
        getCreateContent,
        getEditContent
    };
}

export function GetDrawerContent(drawerProps: AclEntriesDrawerProperties) {
    return AclEntriesDrawerContent.bind(null, drawerProps);
}

export function AclEntriesDrawerContent(
    drawerProps: AclEntriesDrawerProperties,
    editProps: EditDrawerContentProps<AclFormatted>
): JSX.Element {
    const currentEntity = useAppSelector(selectCurrentEntity);
    const currentTenantId = useAppSelector(selectCurrentTenantId);
    const drawerState = useAclEntriesDrawer(drawerProps, editProps.editingItemInfo);

    if (drawerProps.aclPage === AclEntriesPage.UserProfile) {
        if (currentEntity !== DrawerEntitiesNames.UserAcls || currentTenantId !== editProps.tenant?.id) {
            return <></>;
        }
    }

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return drawerState.getCreateContent();
    } else if (drawerState.action === Operations.Edit) {
        return drawerState.getEditContent();
    }

    return <></>;
}
